import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Server, Network, HardDrive, Cloud, Wrench, Code } from 'lucide-react';

const skillCategories = [
  {
    title: 'Enterprise Infrastructure & Deployment',
    icon: Server,
    variant: 'infra',
    skills: [
      'Large-scale facility builds',
      'IT deployments',
      'Hardware installation',
      'Staging & lifecycle support',
      'Multi-floor infrastructure rollouts'
    ]
  },
  {
    title: 'Networking & Security',
    icon: Network,
    variant: 'network',
    skills: [
      'TCP/IP',
      'VLANs',
      'LAN/WAN',
      'Routing & switching',
      'Cisco switches',
      'Juniper firewalls',
      'Network segmentation'
    ]
  },
  {
    title: 'Servers & Virtualization',
    icon: HardDrive,
    variant: 'server',
    skills: [
      'Physical servers',
      'Windows Server',
      'Linux Server',
      'VMware ESXi',
      'vSphere',
      'vCenter',
      'Hyper-V'
    ]
  },
  {
    title: 'Cabling, Hardware & OT Systems',
    icon: Wrench,
    variant: 'hardware',
    skills: [
      'Structured cabling (fiber/copper)',
      'Rack & stack',
      'Patch panels',
      'Industrial networking',
      'PLC-connected systems',
      'Conveyance controls'
    ]
  },
  {
    title: 'Cloud & Identity',
    icon: Cloud,
    variant: 'cloud',
    skills: [
      'AWS EC2',
      'AWS IAM',
      'AWS VPC',
      'Azure Active Directory',
      'Identity management',
      'Access management'
    ]
  },
  {
    title: 'Tools & Scripting',
    icon: Code,
    variant: 'tools',
    skills: [
      'Excel (formulas & data)',
      'Documentation',
      'SOP development',
      'Python (basic)',
      'JavaScript/TypeScript',
      'HTML/CSS'
    ]
  }
];

export const SkillsSection = () => {
  return (
    <section id="skills" className="py-20 md:py-28 bg-secondary/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Core Competencies
          </h2>
          <div className="w-20 h-1 bg-primary mx-auto rounded-full mb-6" />
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            Technical skills and expertise developed through hands-on experience 
            in enterprise IT environments.
          </p>
        </div>

        {/* Skills Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skillCategories.map((category, index) => (
            <Card 
              key={index} 
              className="bg-card border-border hover:shadow-lg transition-shadow"
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-skill-${category.variant}/15`}>
                    <category.icon className={`w-5 h-5 text-skill-${category.variant}`} />
                  </div>
                  <CardTitle className="font-heading text-base font-semibold text-foreground">
                    {category.title}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {category.skills.map((skill, skillIndex) => (
                    <Badge 
                      key={skillIndex} 
                      variant={category.variant}
                      className="skill-badge"
                    >
                      {skill}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Troubleshooting & Operations highlight */}
        <Card className="mt-8 bg-card border-primary/20 shadow-lg">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
              <div className="w-14 h-14 rounded-xl bg-primary-soft flex items-center justify-center flex-shrink-0">
                <Wrench className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-xl text-foreground mb-2">
                  Troubleshooting & Operations
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Hands-on fault isolation and issue resolution, network and systems troubleshooting, 
                  hardware replacement and incident response, with on-call support experience for 
                  production environments.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default SkillsSection;
